import { Trans, t } from '@lingui/macro';
import { Settings, X, Loader2 } from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { useState, useEffect } from "react";
import { useHistory } from "../../hooks/useRouterCompat";
import { useAppUIContext } from "../../context/AppUIContext";
import { useMyPermissions } from "../../context/WorkspacePermissionContext";
import { Room } from "../../models/Room";
import { Workspace } from "../../models/Workspace";
import { UUID } from "../../models/local/Uid";
import { Fire } from "../../services/FireClient";
import { My } from "../../services/My";
import { Util } from "../../services/Utils/Util";
import { UtilApp } from "../../services/Utils/UtilApp";
import { cn } from "../../lib/utils";
import ProfileImg from "./ProfileImg";
import RoomForm from "./RoomFormPart";
import { RoomCall } from '../../models/RoomCall';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { useToast } from "../../components/ui/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Label } from "../../components/ui/label";
import { Card } from "../../components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "../../components/ui/avatar";
import { Users, Calendar, ChevronRight, ImagePlus, User } from "lucide-react";

interface RoomSettingsModalProps {
  room: Room;
  headerAction?: React.ReactNode;
}

export const RoomSettingsModal: React.FC<RoomSettingsModalProps> = ({ room, headerAction }) => {
  const { workspaces } = useAppUIContext();
  const [roomCopy, setRoomCopy] = useState<Room | null>(null);
  const [inProgress, setInProgress] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const { toast } = useToast();
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const isCreator = room?.creatorId && room.creatorId == My.user?.id;
  const perms = useMyPermissions();
  // 삭제 권한: 자신의 방이면 canDeleteOwnRoom, 타인 방이면 canDeleteAnyRoom
  const canDelete = isCreator
    ? (perms?.canDeleteOwnRoom !== false)
    : (perms?.canDeleteAnyRoom === true);
  const [uploadedImage, setUploadedImage] = useState<File | null>(null);
  const [workspace, setWorkspace] = useState<Workspace>();
  const history = useHistory();
  const [call, setCall] = useState<RoomCall>();
  const [creatorName, setCreatorName] = useState<string>('');

  useEffect(() => {
    if (isCreator) {
      setCreatorName(My.user.name);
      return;
    }

    const cachedUser = My.getUserFromRooms(room.creatorId);
    if (cachedUser) {
      setCreatorName(cachedUser.name);
      return;
    }

    // 방 객체에 유저 정보가 있을 경우
    if (room.users) {
      const u = room.users.find(u => u.id === room.creatorId);
      if (u) {
        setCreatorName(u.name);
        return;
      }
    }

    Fire.getUser(room.creatorId).then(u => {
      if (u) setCreatorName(u.name);
      else setCreatorName(t`Unknown`);
    }).catch(() => setCreatorName(t`Unknown`));

  }, [room.creatorId, isCreator, room.users]);

  async function openInfoModal() {
    setRoomCopy(Util.pojo(room));
    setUploadedImage(null);
    setWorkspace(await My.getOrFetchWorkspace(room.workspaceId, workspaces));
    setShowInfoModal(true);
  }

  async function openEditModal() {
    setRoomCopy(Util.pojo(room));
    setUploadedImage(null);
    setWorkspace(await My.getOrFetchWorkspace(room.workspaceId, workspaces));
    setShowEditModal(true);
  }

  return (
    <>
      {/* 룸 프로필 카드 - 클릭 가능한 영역 */}
      {/* 룸 프로필 (WSAdminSidebarContent 스타일) */}
      <div
        className={cn(
          "px-4 pt-4 pb-[22px] border-b border-border cursor-pointer hover:bg-accent transition-colors bg-background relative",
        )}
        onClick={openInfoModal}
      >
        <div className={cn("flex items-center gap-3 w-full", headerAction && "pr-8")}>
          {/* 아바타 */}
          <Avatar className="w-10 h-10 shrink-0">
            {room.profileImageUrl ? (
              <AvatarImage src={room.profileImageUrl} />
            ) : null}
            <AvatarFallback className="bg-muted text-muted-foreground text-sm font-semibold">
              {room.name?.charAt(0)?.toUpperCase() || '#'}
            </AvatarFallback>
          </Avatar>

          <div className="flex flex-col min-w-0 flex-1">
            {/* 룸 이름 */}
            <span className="font-bold text-heading-sm text-[color:rgb(var(--text-title))] truncate leading-normal">
              # {room.name}
            </span>

            {/* 메타 정보 & 설정 버튼 */}
            <div className="flex items-center gap-1.5 text-xs min-w-0 mt-0.5">
              <User className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />
              <span className="truncate font-medium text-muted-foreground">
                {creatorName || t`Unknown`}
              </span>
              {isCreator && (
                <Badge className="shrink-0 text-meta-sm h-4 px-1.5 font-medium bg-primary text-on-primary-text hover:bg-primary border-0">
                  Owner
                </Badge>
              )}

              {/* 설정 버튼 */}
              {isCreator && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    openEditModal();
                  }}
                  className="h-6 w-6 text-muted-foreground hover:text-[color:rgb(var(--text-body))] shrink-0 ml-0.5"
                >
                  <Settings className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>

            {/* 룸 설명 */}
            {room.description && (
              <div
                className="mt-1 text-xs text-muted-foreground line-clamp-2 break-words"
                dangerouslySetInnerHTML={{ __html: UtilApp.getTextAsHTML(room.description, My.user.id) }}
              />
            )}
          </div>
        </div>

        {/* Header Action (e.g. Close Button) */}
        {headerAction && (
          <div
            className="absolute top-2 right-2 z-10"
            onClick={(e) => e.stopPropagation()}
          >
            {headerAction}
          </div>
        )}
      </div>


      {/* 정보 보기 모달 (읽기 전용) */}
      < Dialog open={showInfoModal} onOpenChange={setShowInfoModal} >
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto flex flex-col p-0 gap-0">
          <DialogHeader className="p-4 border-b shrink-0">
            <DialogTitle className="text-lg font-semibold"># {room.name}</DialogTitle>
          </DialogHeader>

          {roomCopy && (
            <div className="flex-1 overflow-y-auto p-6">
              {viewOnlyBody()}
            </div>
          )}

          {roomCopy && (
            <DialogFooter className="px-6 py-3 bg-muted/50 border-t shrink-0">
              <span className="text-xs text-muted-foreground">
                <Trans>Channel was created {UtilApp.dateStrToYmd(roomCopy.created, My.user.lang)}</Trans>
              </span>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog >

      {/* 편집 모달 (방장 전용) */}
      < Dialog open={showEditModal} onOpenChange={setShowEditModal} >
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto flex flex-col p-0 gap-0">
          {inProgress && (
            <div className="absolute top-0 left-0 w-full h-1 bg-muted overflow-hidden z-50">
              <div className="h-full bg-primary animate-progress-indeterminate origin-left-right"></div>
            </div>
          )}

          <DialogHeader className="px-6 py-4 border-b flex flex-row items-center justify-between space-y-0 shrink-0 bg-background sticky top-0 z-10">
            <DialogTitle className="truncate text-lg font-semibold"># {room.name}</DialogTitle>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowEditModal(false)}
              className="h-8 w-8 text-muted-foreground hover:text-[color:rgb(var(--text-body))]"
            >
              <X className="w-5 h-5" />
            </Button>
          </DialogHeader>

          {roomCopy && (
            <div className="flex-1 overflow-y-auto p-6">
              <RoomForm room={roomCopy} onUploadImageSelected={setUploadedImage} />
            </div>
          )}

          {roomCopy && !roomCopy.isOneToOne && (
            <DialogFooter className="px-6 py-3 bg-muted/50 border-t shrink-0 flex-row justify-between items-center gap-2">
              {canDelete && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowDeleteAlert(true)}
                  className="text-destructive hover:text-destructive hover:bg-destructive/10"
                >
                  <Trans>Delete</Trans>
                </Button>
              )}
              <Button onClick={saveRoomDetails} disabled={inProgress} size="sm">
                {inProgress && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                <Trans>Save changes</Trans>
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog >

      <AlertDialog open={showDeleteAlert} onOpenChange={setShowDeleteAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle><Trans>Delete this channel</Trans></AlertDialogTitle>
            <AlertDialogDescription>
              <Trans>Are you sure to delete this channel?</Trans>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel><Trans>No</Trans></AlertDialogCancel>
            <AlertDialogAction onClick={deleteRoom} className="bg-destructive hover:bg-destructive/90">
              <Trans>Delete now</Trans>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );

  function viewOnlyBody() {
    if (roomCopy)
      return (
        <div className="space-y-6">
          <div className="space-y-2">
            <Label className="text-sm font-medium text-muted-foreground"><Trans>Profile image</Trans></Label>
            <div className="mt-1">
              {!roomCopy.profileImageUrl ? (
                <div className="text-muted-foreground text-sm italic"><Trans>No image</Trans></div>
              ) : (
                <div className="w-24 h-24 rounded-lg overflow-hidden border border-border">
                  <ProfileImg url={roomCopy.profileImageUrl} className="w-full h-full object-cover" />
                </div>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium text-muted-foreground"><Trans>Channel description</Trans></Label>
            <div className="mt-1 p-3 bg-muted/50 rounded-md text-sm text-[color:rgb(var(--text-body))] min-h-[60px]">
              {roomCopy.description ? (
                <div dangerouslySetInnerHTML={{ __html: UtilApp.getTextAsHTML(roomCopy.description, My.user.id) }}></div>
              ) : (
                <div className="text-muted-foreground italic"><Trans>No description</Trans></div>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium text-muted-foreground"><Trans>Workspace</Trans></Label>
            <div className="mt-1 text-sm text-[color:rgb(var(--text-body))]">
              {workspace ? workspace.name : <Trans>No Workspace</Trans>}
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium text-muted-foreground"><Trans>Visibility</Trans></Label>
            <div className="mt-1 text-sm text-[color:rgb(var(--text-body))]">
              {roomCopy.visibility == 'listed' ? <Trans>Listed in workspace</Trans> : <Trans>Unlisted</Trans>}
            </div>
          </div>
        </div>
      );
  }

  async function saveRoomDetails() {
    if (roomCopy) {
      setInProgress(true);

      if (uploadedImage) {
        Fire.uploadFile(uploadedImage, `/rooms/${roomCopy.id}/cover/${UUID.get()}.jpg`, url => {
          roomCopy.profileImageUrl = url;
          saveRoomDetails2(roomCopy);
        }, null);
      } else {
        saveRoomDetails2(roomCopy);
      }
    }
  }

  async function saveRoomDetails2(r: Room) {
    await Fire.updateRoomDetails(r);
    setInProgress(false);
    setShowEditModal(false);
    toast({ title: t`Channel updated`, duration: 2000 });
  }

  async function deleteRoom() {
    setShowEditModal(false);
    if (roomCopy) {
      setTimeout(async () => {
        await Fire.deleteRoom(roomCopy.id);
        navigateSomewhereElse();
        toast({
          title: t`Channel deleted`,
          duration: 2000,
        });
      }, 500)
    }
  }

  function navigateSomewhereElse() {
    let otherRooms = Array.from(My.Rooms.values()).filter(r => r.id != roomCopy?.id);
    if (otherRooms.length > 0) {
      history.replace('/channel/' + otherRooms[0].id);
    } else {
      history.replace('/welcome')
    }
  }
}

export default RoomSettingsModal;

